import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  varchar,
  text,
  date,
  boolean,
  timestamp,
  mysqlEnum,
  index,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";

/**
 * Batch 1.2 — HR core. Ported line-by-line from the legacy base migration + standalone files.
 * `teams` = the "departments" entity (employee_details.department_id → teams). Audit columns
 * (added_by/last_updated_by) preserved everywhere; they drive RBAC scopes (reskin-docs/03 §1).
 *
 * Sources: base migration teams L566–575, designations L745–754, skills L961–967,
 * employee_skills L969–978, employee_teams L980–989, employee_docs L892–907,
 * emergency_contacts L845–862, holidays L1214–…; employee_document_expiries
 * (2025_10_09_060310_*), award_icons/awards/appreciations (2022_09_01_060824_*),
 * promotions (2024_09_06_093758_*).
 */

export const teams = mysqlTable("teams", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  teamName: varchar("team_name", { length: 255 }).notNull(),
  // 2022_08_12 create_other_migration_till_date (after team_name)
  parentId: int("parent_id", { unsigned: true }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const designations = mysqlTable("designations", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  // 2022_08_26 add_parent_id_column_designation_table (after name)
  parentId: int("parent_id", { unsigned: true }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const skills = mysqlTable("skills", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 200 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeeSkills = mysqlTable("employee_skills", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  skillId: int("skill_id", { unsigned: true })
    .notNull()
    .references(() => skills.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeeTeams = mysqlTable("employee_teams", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  teamId: int("team_id", { unsigned: true })
    .notNull()
    .references(() => teams.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const employeeDocs = mysqlTable("employee_docs", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  name: varchar("name", { length: 200 }).notNull(),
  filename: varchar("filename", { length: 200 }).notNull(),
  hashname: varchar("hashname", { length: 200 }).notNull(),
  size: varchar("size", { length: 200 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const emergencyContacts = mysqlTable("emergency_contacts", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  name: varchar("name", { length: 255 }),
  email: varchar("email", { length: 255 }),
  mobile: varchar("mobile", { length: 255 }),
  relation: varchar("relation", { length: 255 }),
  address: varchar("address", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const holidays = mysqlTable(
  "holidays",
  {
    id: int("id", { unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    date: date("date").notNull(),
    occassion: varchar("occassion", { length: 100 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    eventId: text("event_id"),
    // 2024_03_12 add_columns_fix_to_holidays_table (department_id/designation_id/employment_type
    // were added-then-dropped here; never present in this schema → only the *_json cols remain)
    departmentIdJson: text("department_id_json"),
    designationIdJson: text("designation_id_json"),
    employmentTypeJson: text("employment_type_json"),
    // 2024_06_20 add_column_in_holiday_table
    notificationSent: mysqlEnum("notification_sent", ["yes", "no"]).default("no"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("holidays_date_index").on(t.date)],
);

export const employeeDocumentExpiries = mysqlTable("employee_document_expiries", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  documentName: varchar("document_name", { length: 255 }).notNull(),
  documentNumber: varchar("document_number", { length: 255 }),
  issueDate: date("issue_date").notNull(),
  expiryDate: date("expiry_date").notNull(),
  alertBeforeDays: int("alert_before_days").default(30).notNull(),
  alertEnabled: boolean("alert_enabled").default(true).notNull(),
  filename: varchar("filename", { length: 255 }),
  hashname: varchar("hashname", { length: 255 }),
  size: varchar("size", { length: 255 }),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** award_icons — GLOBAL catalogue (no company_id). */
export const awardIcons = mysqlTable("award_icons", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  icon: varchar("icon", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const awards = mysqlTable("awards", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  title: varchar("title", { length: 255 }).notNull(),
  awardIconId: bigint("award_icon_id", { mode: "number", unsigned: true }).references(
    () => awardIcons.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  summary: text("summary"),
  status: mysqlEnum("status", ["active", "inactive"]).default("active").notNull(),
  colorCode: varchar("color_code", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const appreciations = mysqlTable("appreciations", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  awardId: bigint("award_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => awards.id, { onDelete: "cascade", onUpdate: "cascade" }),
  awardTo: int("award_to", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  awardDate: date("award_date").notNull(),
  image: varchar("image", { length: 255 }),
  summary: text("summary"),
  addedBy: int("added_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const promotions = mysqlTable("promotions", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  employeeId: int("employee_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  date: date("date"),
  previousDesignationId: bigint("previous_designation_id", {
    mode: "number",
    unsigned: true,
  }).references(() => designations.id, { onDelete: "cascade", onUpdate: "cascade" }),
  currentDesignationId: bigint("current_designation_id", {
    mode: "number",
    unsigned: true,
  }).references(() => designations.id, { onDelete: "cascade", onUpdate: "cascade" }),
  sendNotification: mysqlEnum("send_notification", ["yes", "no"]).default("no").notNull(),
  previousDepartmentId: int("previous_department_id", { unsigned: true }).references(
    () => teams.id,
    { onDelete: "set null", onUpdate: "cascade" },
  ),
  currentDepartmentId: int("current_department_id", { unsigned: true }).references(() => teams.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // 2025_07_02 alter_promotions_table
  isSend: tinyint("is_send").default(1),
  // 2025_07_10 alter_promotions_table
  promotion: tinyint("promotion").default(1),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * compliance_items — HR compliance/deadline tracker (legacy ComplianceItem; migration
 * 2026_06_03_181236). NET-NEW to our schema (B2 plan flagged it as a schema-port follow-up).
 * `completed_at` null = still open. `type` ∈ training/verification/review/medical/other.
 */
export const complianceItems = mysqlTable(
  "compliance_items",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade", onUpdate: "cascade" }),
    title: varchar("title", { length: 191 }).notNull(),
    description: text("description"),
    type: varchar("type", { length: 32 }).default("other").notNull(),
    dueDate: date("due_date").notNull(),
    ownerId: int("owner_id", { unsigned: true }).references(() => users.id, { onDelete: "set null", onUpdate: "cascade" }),
    completedAt: timestamp("completed_at"),
    createdBy: int("created_by", { unsigned: true }).references(() => users.id, { onDelete: "set null", onUpdate: "cascade" }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("compliance_company_due").on(t.companyId, t.dueDate),
    index("compliance_company_done").on(t.companyId, t.completedAt),
  ],
);
